// src/hooks/dashboard/useDashboardData.ts

import { useEffect } from "react";
import { useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useCategoriesTree } from "@/hooks/dashboard/useCategoriesTree";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import type { DashboardType, DashboardDataType } from "@/types/dashboards/dashboardTypes";
import type { DashboardResponseWithData } from "@/types/dashboardResponse";
import type { GenericRow } from "@/utils/dashboard/dashboardDataAdapter";
import {
  buildMetricsQueryFn,
  buildCurrentQueryFn,
  buildRatiosQueryFn,
  buildPercentageChangesQueryFn,
  buildRollupsQueryFn,
} from "./queryBuilders/dashboardQueryBuilders";
import { prefetchDashboardData, prefetchCategoryData, prefetchChartsData } from "./prefetch/dashboardPrefetch";

// ============================================================================
// Types
// ============================================================================

type DashboardDataResponse = DashboardResponseWithData<GenericRow>;

interface UseDashboardDataOptions {
  simulateError?: boolean;
  simulateLoading?: boolean;
  loadingDelay?: number;
}

// ============================================================================
// Main Hook
// ============================================================================

/**
 * Fetches dashboard data for a specific dashboard type and data type
 * 
 * @param dashboardType - The dashboard category (e.g., 'layer1', 'defi')
 * @param dataType - The type of data to fetch ('current', 'metrics', 'ratios', etc.)
 * @param options - Optional configuration for testing/simulation
 * 
 * @example
 * const { data, isLoading } = useDashboardData('layer1', 'current');
 */
export const useDashboardData = (
  dashboardType: DashboardType,
  dataType: DashboardDataType,
  options: UseDashboardDataOptions = {}
): UseQueryResult<DashboardDataResponse> => {
  const { simulateError = false, simulateLoading = false, loadingDelay = 0 } = options;

  // Ensure dependencies are loaded
  useMetricsCatalog();
  const { maps } = useCategoriesTree();
  
  const categoryId = maps.codeToId[String(dashboardType)] ?? -1;
  const queryKey = ["dashboard", "data", { dashboardType, dataType, categoryId }] as const;

  return useQuery({
    queryKey,
    enabled: categoryId > 0,
    queryFn: async (context) => {
      const signal = context.signal;

      // Test utilities
      if (simulateLoading && loadingDelay > 0) {
        await new Promise((resolve) => setTimeout(resolve, loadingDelay));
      }
      if (simulateError) {
        throw new Error(`Simulated error for ${dashboardType} ${dataType}`);
      }

      // Route to appropriate query builder
      switch (dataType) {
        case "metrics":
          return buildMetricsQueryFn(categoryId, signal);
        case "current":
          return buildCurrentQueryFn(categoryId, signal);
        case "ratios":
          return buildRatiosQueryFn(categoryId, signal);
        case "percentageChanges":
          return buildPercentageChangesQueryFn(categoryId, signal);
        case "rollups":
          return buildRollupsQueryFn(categoryId, signal);
        default:
          return { data: [], lastUpdated: new Date().toISOString() } as DashboardDataResponse;
      }
    },
  }) as UseQueryResult<DashboardDataResponse>;
};

// ============================================================================
// Convenience Hooks
// ============================================================================

export const useDashboardMetrics = (
  dashboardType: DashboardType,
  options?: UseDashboardDataOptions
) => useDashboardData(dashboardType, "metrics", options);

export const useDashboardPercentageChanges = (
  dashboardType: DashboardType,
  options?: UseDashboardDataOptions
) => useDashboardData(dashboardType, "percentageChanges", options);

export const useDashboardRatios = (
  dashboardType: DashboardType,
  options?: UseDashboardDataOptions
) => useDashboardData(dashboardType, "ratios", options);

// ============================================================================
// Prefetch Hook
// ============================================================================

/**
 * Prefetches all dashboard data when a dashboard loads
 * 
 * Prefetches:
 * - Dashboard table data (current, changes, rollups)
 * - Category-level data (snapshots, performance, sparklines)
 * - Chart data for first page (4 metrics + 3 ratios)
 * 
 * @param dashboardType - The dashboard to prefetch data for
 */
export function useSectorDashboardPrefetch(dashboardType: DashboardType) {
  const queryClient = useQueryClient();
  const { maps } = useCategoriesTree();

  useEffect(() => {
    if (!dashboardType) return;

    const categoryId = maps.codeToId[String(dashboardType)] ?? -1;
    if (categoryId <= 0) return;

    // Prefetch data in priority order:
    // 1. Category data (overview components)
    // 2. Dashboard table data
    // 3. Charts (lower priority)
    Promise.allSettled(prefetchCategoryData(queryClient, categoryId))
      .then(() => Promise.allSettled(prefetchDashboardData(queryClient, dashboardType, categoryId)))
      .then(() => prefetchChartsData(queryClient, categoryId))
      .catch((error) => {
        console.error(`Dashboard prefetch failed for ${dashboardType}:`, error);
      });
  }, [dashboardType, queryClient, maps.codeToId]);
}

// ============================================================================
// Exports
// ============================================================================

export type { DashboardDataType };
